<nav class="relative bg-transparent px-4 sm:px-6 lg:px-10">
  <div class="mx-auto flex h-33.25 w-full max-w-350 items-start py-5">
    <div class="flex h-13.5 w-full items-center justify-between">
      <!-- Logo -->
      <a routerLink="/" class="flex items-center gap-4 sm:gap-6" (click)="closeMobileAuthMenu()">
        <img
          ngSrc="assets/images/logo.png"
          alt="Spare Cores"
          width="454"
          height="211"
          priority
          class="h-9 w-auto opacity-100 sm:h-13.5"
        />
        <span
          class="font-mono text-[20px] font-normal leading-none tracking-[0.045em] text-teal-500 sm:text-[30px]"
          >&lt;<span class="text-white">sentinel</span>&gt;</span
        >
      </a>

      <!-- Center nav links (signed in only) -->
      @if (auth.isSignedIn()) {
        <div class="hidden items-center gap-8 md:flex">
          <a
            routerLink="/getting-started"
            routerLinkActive="text-white"
            class="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors"
          >
            <lucide-icon name="compass" [size]="14"></lucide-icon>
            Getting Started
          </a>
          <!-- Runs dropdown -->
          <div class="relative">
            <button
              type="button"
              (click)="toggleRunsMenu()"
              class="flex cursor-pointer items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors"
            >
              <lucide-icon name="monitor" [size]="14"></lucide-icon>
              Runs
              <lucide-icon name="chevron-down" [size]="12"></lucide-icon>
            </button>
            @if (runsMenuOpen()) {
              <!-- Backdrop -->
              <div
                class="fixed inset-0 z-40"
                data-click-target="true"
                (click)="closeRunsMenu()"
              ></div>
              <!-- Menu -->
              <div
                class="absolute left-0 top-full mt-2 w-52 bg-surface-dark border border-gray-700 rounded-lg shadow-lg z-50 py-1"
              >
                <a
                  routerLink="/runs"
                  (click)="closeRunsMenu()"
                  class="flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
                >
                  <lucide-icon name="list" [size]="16"></lucide-icon>
                  Recent runs
                </a>
                <a
                  routerLink="/run-groups"
                  (click)="closeRunsMenu()"
                  class="flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
                >
                  <lucide-icon name="layers" [size]="16"></lucide-icon>
                  Recent run groups
                </a>
              </div>
            }
          </div>
        </div>
      }

      <!-- Right side -->
      <div class="relative flex items-center gap-2">
        @if (!auth.isSignedIn()) {
          <div class="hidden items-center gap-2 sm:flex">
            <button
              type="button"
              (click)="signIn()"
              data-ph-capture-attribute-sc-event="login started"
              class="btn-white"
            >
              <lucide-icon name="user-round-check" [size]="14"></lucide-icon>
              Log in
            </button>
            <button
              type="button"
              (click)="signUp()"
              data-ph-capture-attribute-sc-event="registration started"
              class="btn-primary"
            >
              Register
              <lucide-icon name="user-round-plus" [size]="14"></lucide-icon>
            </button>
          </div>

          <button
            type="button"
            class="relative z-50 inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-700 bg-surface-dark/60 text-gray-300 transition-colors hover:bg-white/5 hover:text-white focus:outline-none focus:ring-2 focus:ring-accent/50 sm:hidden"
            aria-controls="mobile-auth-menu"
            [attr.aria-expanded]="mobileAuthMenuOpen()"
            aria-haspopup="true"
            (click)="toggleMobileAuthMenu()"
          >
            <span class="sr-only">{{
              mobileAuthMenuOpen() ? 'Close account menu' : 'Open account menu'
            }}</span>

            @if (mobileAuthMenuOpen()) {
              <lucide-icon name="x" [size]="20" aria-hidden="true"></lucide-icon>
            } @else {
              <span aria-hidden="true" class="flex h-4 w-5 flex-col justify-between">
                <span class="block h-0.5 w-full rounded-full bg-current"></span>
                <span class="block h-0.5 w-full rounded-full bg-current"></span>
                <span class="block h-0.5 w-full rounded-full bg-current"></span>
              </span>
            }
          </button>

          @if (mobileAuthMenuOpen()) {
            <div
              class="fixed inset-0 z-40 sm:hidden"
              data-click-target="true"
              (click)="closeMobileAuthMenu()"
            ></div>

            <div
              id="mobile-auth-menu"
              class="absolute right-0 top-full z-50 mt-3 flex w-[min(16rem,calc(100vw-2rem))] flex-col gap-3 rounded-xl border border-gray-700 bg-surface-dark/95 p-3 shadow-lg backdrop-blur sm:hidden"
            >
              <button
                type="button"
                (click)="signIn()"
                data-ph-capture-attribute-sc-event="login started"
                class="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-[15px] font-semibold leading-[18px] tracking-[0.005em] text-gray-800 shadow-sm transition-colors hover:bg-gray-100/80 focus:outline-none focus:ring-2 focus:ring-accent/50"
              >
                <lucide-icon name="user-round-check" [size]="14"></lucide-icon>
                Log in
              </button>

              <button
                type="button"
                (click)="signUp()"
                data-ph-capture-attribute-sc-event="registration started"
                class="btn-primary w-full justify-center"
              >
                Register
                <lucide-icon name="user-round-plus" [size]="14"></lucide-icon>
              </button>
            </div>
          }
        } @else {
          <!-- Organization switcher (Clerk) -->
          @if (auth.hasMultipleOrganizations()) {
            <app-organization-switcher />
          }

          <!-- User dropdown -->
          <div class="relative">
            <button
              (click)="toggleDropdown()"
              class="flex cursor-pointer items-center gap-2.5 rounded-lg bg-surface-dark/60 border border-accent px-3 py-1.5 text-sm text-gray-300 hover:text-white transition-colors"
            >
              @if (auth.userImageUrl()) {
                <img
                  [src]="auth.userImageUrl()"
                  [alt]="auth.userName()"
                  class="w-7 h-7 rounded-full border border-gray-600"
                />
              } @else {
                <div class="w-7 h-7 rounded-full bg-gray-600 flex items-center justify-center">
                  <lucide-icon name="user" [size]="14"></lucide-icon>
                </div>
              }
              <div class="hidden sm:block text-left">
                <div class="text-sm font-medium text-white leading-tight">
                  {{ auth.userName() }}
                </div>
              </div>
              <lucide-icon name="chevron-down" [size]="14" class="text-gray-500"></lucide-icon>
            </button>

            @if (dropdownOpen()) {
              <!-- Backdrop -->
              <div
                class="fixed inset-0 z-40"
                data-click-target="true"
                (click)="closeDropdown()"
              ></div>

              <!-- Dropdown menu -->
              <div
                class="absolute right-0 top-full mt-2 w-56 bg-surface-dark border border-gray-700 rounded-lg shadow-lg z-50 py-1"
              >
                <button
                  (click)="openProfile()"
                  class="w-full flex cursor-pointer items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
                >
                  <lucide-icon name="user" [size]="16"></lucide-icon>
                  Edit Profile
                </button>

                <a
                  routerLink="/api-keys"
                  (click)="closeDropdown()"
                  class="flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
                >
                  <lucide-icon name="key" [size]="16"></lucide-icon>
                  API Keys
                </a>

                <div class="border-t border-gray-700 my-1"></div>

                <button
                  (click)="signOut()"
                  data-ph-capture-attribute-sc-event="logout started"
                  class="w-full flex cursor-pointer items-center gap-3 px-4 py-2 text-sm text-red-400 hover:bg-white/5 hover:text-red-300 transition-colors"
                >
                  <lucide-icon name="log-out" [size]="16"></lucide-icon>
                  Sign Out
                </button>
              </div>
            }
          </div>
        }
      </div>
    </div>
  </div>
</nav>
